import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { Pagination } from 'antd'
import { Loader } from '../../../components/Loader'
import { fetchDispatchDocumentRecipientsHistory } from '../../../services'

const T_PREFIX = 'dispatch_recipients'
const ACTION_STYLES = { create: 'bg-green-100 text-green-800', update: 'bg-blue-100 text-blue-800', delete: 'bg-red-100 text-red-800' }

const describe = (snapshot, allCarriers) => (snapshot ? `${snapshot.name} <${snapshot.email}> · ${snapshot.carrier_name || allCarriers}` : null)

// Who added, changed or removed which recipient, newest first.
export const RecipientsHistoryModal = ({ open, setOpen, warehouseId }) => {
  const { i18n } = useTranslation()
  const [page, setPage] = useState(1)
  const { data, isLoading, isError, refetch } = useQuery(
    ['dispatch_document_recipients_history', warehouseId, page],
    () => fetchDispatchDocumentRecipientsHistory(warehouseId, { page, per_page: 20 }),
    { enabled: open && !!warehouseId, keepPreviousData: true, refetchOnWindowFocus: false },
  )
  // Loading, failed and empty are three different answers: a failed read is not "no changes".
  const entries = data?.history
  const allCarriers = i18n.t(`${T_PREFIX}.all_carriers`)

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={() => setOpen(false)}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                {i18n.t(`${T_PREFIX}.history.title`)}
              </Dialog.Title>
              <button type="button" className="text-gray-400 hover:text-gray-500" onClick={() => setOpen(false)} aria-label={i18n.t(`${T_PREFIX}.form.cancel`)}>
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            {isLoading || (!isError && !entries) ? (
              <Loader show={true} />
            ) : isError ? (
              <p role="alert" className="text-sm text-gray-500 py-6 text-center">
                {i18n.t(`${T_PREFIX}.history.load_error`)}{' '}
                <button type="button" className="text-indigo-600 hover:underline" onClick={() => refetch()}>
                  {i18n.t(`${T_PREFIX}.actions.retry`)}
                </button>
              </p>
            ) : entries.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">{i18n.t(`${T_PREFIX}.history.empty`)}</p>
            ) : (
              <ul className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {entries.map(entry => (
                  <li key={entry.id} className="py-3 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_STYLES[entry.action] || 'bg-gray-100 text-gray-800'}`}
                      >
                        {i18n.t(`${T_PREFIX}.history.action_${entry.action}`)}
                      </span>
                      <span className="text-gray-500">
                        {new Date(entry.created_at).toLocaleString(i18n.language)} · {entry.created_by?.name || '—'}
                      </span>
                    </div>
                    {entry.before_change && entry.after_change ? (
                      <div className="mt-1 text-gray-700">
                        <span className="line-through text-gray-400">{describe(entry.before_change, allCarriers)}</span> →{' '}
                        {describe(entry.after_change, allCarriers)}
                      </div>
                    ) : (
                      <div className="mt-1 text-gray-700">{describe(entry.after_change || entry.before_change, allCarriers)}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {(data?.meta?.total_pages || 1) > 1 && (
              <div className="mt-4 flex justify-end">
                <Pagination size="small" current={page} total={data.meta.total_count} pageSize={20} onChange={setPage} showSizeChanger={false} />
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
